import { hookReportSchema, hookReportResponseSchema, hookTableResponseSchema, listHooksDataTableParamsSchema, hookContentResponseSchema, listHookUsersResponseSchema, listHookProjectsResponseSchema, hookFilterOptionsQuerySchema, hookFilterOptionsResponseSchema, } from '../contracts/hooks';
import { serializeTableQueryParams } from '../contracts/table';
import { parseSchema } from '../errors';
import { organizationIdSchema, } from '../contracts/organizations';
/**
 * Report a batch of discovered hooks to the account-scoped ingestion route.
 * Used by the Go agent and the VS Code extension family — both authenticate
 * via API key.
 */
export async function reportAccountHooks(client, payload) {
    const validated = parseSchema(hookReportSchema, payload);
    const response = await client.post('/account/hooks', validated);
    return parseSchema(hookReportResponseSchema, response);
}
/**
 * Report a batch of discovered hooks to the org-scoped ingestion route.
 */
export async function reportOrganizationHooks(client, organizationId, payload) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validated = parseSchema(hookReportSchema, payload);
    const response = await client.post(`/organizations/${validatedId}/hooks`, validated);
    return parseSchema(hookReportResponseSchema, response);
}
/**
 * List hooks in DataTable format. Mirrors `listAiInstructionsDataTable`.
 */
export async function listHooksDataTable(client, organizationId, params = {}, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(listHooksDataTableParamsSchema, params);
    const response = await client.get(`/organizations/${validatedId}/hooks`, {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(hookTableResponseSchema, response);
}
/**
 * Get distinct filter-option values for a server-backed hook field.
 * Supported fields: family, platform, file, projects.
 */
export async function getHookFilterOptions(client, organizationId, params) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(hookFilterOptionsQuerySchema, params);
    const response = await client.get(`/organizations/${validatedId}/hooks/filter-options`, { params: validatedParams });
    return parseSchema(hookFilterOptionsResponseSchema, response);
}
/**
 * Fetch the raw hook content for the drawer.
 */
export async function getHookContent(client, organizationId, groupKey) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/hooks/${encodeURIComponent(groupKey)}/content`);
    return parseSchema(hookContentResponseSchema, response);
}
export async function listHookGroupUsers(client, organizationId, groupKey) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/hooks/${encodeURIComponent(groupKey)}/users`);
    return parseSchema(listHookUsersResponseSchema, response);
}
export async function listHookGroupProjects(client, organizationId, groupKey) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/hooks/${encodeURIComponent(groupKey)}/projects`);
    return parseSchema(listHookProjectsResponseSchema, response);
}
// ============================================================================
// Account-scoped (personal) hook listing (KIR-3643). Same shapes as the org
// variants above, minus organizationId — the personal Hooks inventory view.
// ============================================================================
/**
 * List the caller's personal (org-less) hooks in DataTable format.
 */
export async function listAccountHooksDataTable(client, params = {}, options) {
    const validatedParams = parseSchema(listHooksDataTableParamsSchema, params);
    const response = await client.get('/account/hooks', {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(hookTableResponseSchema, response);
}
/**
 * Distinct filter-option values for a personal hook field.
 */
export async function getAccountHookFilterOptions(client, params) {
    const validatedParams = parseSchema(hookFilterOptionsQuerySchema, params);
    const response = await client.get('/account/hooks/filter-options', {
        params: validatedParams,
    });
    return parseSchema(hookFilterOptionsResponseSchema, response);
}
/**
 * Raw hook content for the personal drawer.
 */
export async function getAccountHookContent(client, groupKey) {
    const response = await client.get(`/account/hooks/${encodeURIComponent(groupKey)}/content`);
    return parseSchema(hookContentResponseSchema, response);
}
export async function listAccountHookGroupUsers(client, groupKey) {
    const response = await client.get(`/account/hooks/${encodeURIComponent(groupKey)}/users`);
    return parseSchema(listHookUsersResponseSchema, response);
}
export async function listAccountHookGroupProjects(client, groupKey) {
    const response = await client.get(`/account/hooks/${encodeURIComponent(groupKey)}/projects`);
    return parseSchema(listHookProjectsResponseSchema, response);
}
//# sourceMappingURL=hooks.js.map